import { AnalyticEvents, AnalyticEventName } from './events';

/**
 * What a caller may attach to an event.
 *
 * Two keys because the destinations disagree about names: Meta wants `value`
 * and `currency`, GA4 wants `value`, `currency` and `transaction_id`, and the
 * UTM fields we send Meta have no GA4 equivalent at all. `params` covers GA4
 * and GTM together -- GTM's job here is to forward to GA4, so anything worth
 * sending to one is worth sending to the other under the same name.
 */
export interface TrackUserEventPayload {
  fbq?: Record<string, unknown>;
  params?: Record<string, unknown>;
}

/**
 * Meta says not to send the same event twice in one page view, so we do not.
 * Per page view, deliberately: this resets on navigation, and is a separate
 * concern from the purchase claim in claims.ts, which persists across one.
 */
const metaEventsFired: string[] = [];

export function trackEventMeta(
  eventName: AnalyticEventName,
  payload?: Record<string, unknown>,
): void {
  const event = AnalyticEvents[eventName].meta;

  if (!window.fbq || metaEventsFired.indexOf(event) !== -1) {
    return;
  }

  window.fbq('track', event, payload);
  metaEventsFired.push(event);
}

export function trackEventGa4(
  eventName: AnalyticEventName,
  params?: Record<string, unknown>,
): void {
  // g4_id as well as gtag: GTM injects a `gtag` of its own, so its presence
  // does not mean the organiser configured a GA4 property for us to send to.
  if (!window.gtag || !window.g4_id) {
    return;
  }

  window.gtag('event', AnalyticEvents[eventName].ga4, params || {});
}

export function trackEventGtm(
  eventName: AnalyticEventName,
  params?: Record<string, unknown>,
): void {
  if (!window.dataLayer) {
    return;
  }

  const data: Record<string, unknown> = { event: AnalyticEvents[eventName].gtm };

  // Alongside `event`, never replacing it. The organiser's GTM triggers key on
  // the event name; a parameter that overwrote it would silently unhook every
  // tag they have built, and nothing on our side would look wrong.
  Object.keys(params || {}).forEach(key => {
    if (key !== 'event') {
      data[key] = (params as Record<string, unknown>)[key];
    }
  });

  window.dataLayer.push(data);
}

/** Send one funnel step to all three destinations. */
export function trackUserEvent(
  eventName: AnalyticEventName,
  extraData: TrackUserEventPayload = {},
): void {
  trackEventMeta(eventName, extraData.fbq);
  trackEventGa4(eventName, extraData.params);
  trackEventGtm(eventName, extraData.params);
}

/**
 * The legacy category/action tracking, which predates the funnel events above
 * and still has call sites on both sides -- show_event.html ("Visit Event
 * Page"), venueadmin.js ("Subscription started"), social_tools.html.
 *
 * Mixpanel only, now. It also called `window.ga` (Universal Analytics), which
 * was removed in GATE-512: no UA script is loaded anywhere in this codebase, so
 * `window.ga` was never defined and that call could not have run.
 *
 * Worth knowing what this means: the Pro events above reach Mixpanel and nothing
 * else. None of them is a GA4 event, because Pro pages configure no GA4 property
 * -- see the storefront's analytics/google_analytics.html, which has no Pro
 * counterpart.
 */
export function trackEvent(
  category: string,
  eventName: string,
  attrs?: Record<string, unknown>,
): void {
  if (window.mixpanel) {
    window.mixpanel.track(category + ': ' + eventName, attrs || {});
  }
}

/**
 * How long to leave the beacons before navigating away.
 *
 * `fbq` sends its hit as an image or an XHR, and GTM's tags fire asynchronously
 * off the dataLayer push, so leaving the document in the same tick cancels
 * them. `gtag` is the exception -- it uses sendBeacon, which survives -- which
 * is why this only ever looked like a Meta and GTM problem. The pay step
 * navigates straight to the payment provider, so without this its event is a
 * coin flip.
 */
export const TRACKING_GRACE_MS = 300;

export function gatemeAfterTracking(callback: () => void): void {
  window.setTimeout(callback, TRACKING_GRACE_MS);
}
